import { P } from '@angular/cdk/keycodes';
import { Injectable } from '@angular/core';


// Add better type definitions for Google Maps objects
declare global {
  interface Window {
    google: typeof google;
  }
}

export interface PlacePrediction {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
  lat?: number;
  lng?: number;
}

export interface PlaceLocation {
  lat: number;
  lng: number;
}

export class PlacesError extends Error {
  constructor(message: string, public code: string) {
    super(message);
    this.name = 'PlacesError';
  }
}

@Injectable({
  providedIn: 'root',
})
export class PlacesService {
  private autocompleteSrv?: google.maps.places.AutocompleteService;
  private placesService?: google.maps.places.PlacesService;
  private geocoder?: google.maps.Geocoder;

  constructor() {
    if (typeof window !== 'undefined' && window.google?.maps) {
      this.initializeServices();
    }
  }

  /**
   * Initialize Google Maps services
   */
  private initializeServices(): void {
    try {
      this.autocompleteSrv = new google.maps.places.AutocompleteService();
      this.placesService = new google.maps.places.PlacesService(document.createElement('div'));
      this.geocoder = new google.maps.Geocoder();
    } catch (error) {
      console.error('Failed to initialize Google Maps services:', error);
      throw new PlacesError('Failed to initialize Places service', 'INIT_ERROR');
    }
  }

  /**
   * Ensure services are initialized
   * @throws PlacesError if services are not initialized
   */
  private ensureServicesInitialized(): void {
    if (!this.autocompleteSrv || !this.placesService || !this.geocoder) {
      throw new PlacesError('Google Maps services not initialized', 'SERVICES_NOT_INITIALIZED');
    }
  }

  /**
   * Get place predictions based on search query
   * @param query The search query string
   * @param options Optional parameters for the search
   * @returns Promise resolving to an array of place predictions with locations
   * @throws PlacesError if the API request fails
   */
  async getPlacePredictions(
    query: string,
    options: Partial<google.maps.places.AutocompletionRequest> = {}
  ): Promise<PlacePrediction[]> {
    console.log('Query:', query, ' | Options:', options);

    if (!query || query.length < 3) {
      return [];
    }

    try {
      this.ensureServicesInitialized();

      const request: google.maps.places.AutocompletionRequest = {
        input: query,
        componentRestrictions: { country: 'ma' },
        //types: ['geocode'],
        ...options,
      };

      const predictions = await new Promise<
        google.maps.places.AutocompletePrediction[]
      >((resolve, reject) => {
        this.autocompleteSrv!.getPlacePredictions(
          request,
          (results, status) => {
            if (
              status === google.maps.places.PlacesServiceStatus.OK &&
              results
            ) {
              resolve(results);
            } else {
              reject(status);
            }
          }
        );
      });

      // Get detailed place information for each prediction
      const detailedPredictions = await Promise.all(
        predictions.map((prediction) => this.getPlaceDetails(prediction))
      );
      console.log('Detailed Predictions:', detailedPredictions);
      return detailedPredictions.filter(
        (p): p is PlacePrediction => p !== null
      );
    } catch (error) {
      console.error('Error fetching place predictions:', error);
      return Promise.reject();
      
    }
  }


  /**
   * Get details for a specific place by its ID
   * @param placeId The Google Places ID
   * @returns Promise resolving to the place details
   * @throws PlacesError if the API request fails
   */
  async getPlaceById(placeId: string): Promise<google.maps.places.PlaceResult> {
    try {
      this.ensureServicesInitialized();

      return await new Promise((resolve, reject) => {
        const request: google.maps.places.PlaceDetailsRequest = {
          placeId,
          fields: ['geometry', 'formatted_address', 'name', 'place_id']
        };

        this.placesService!.getDetails(request, (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place) {
            resolve(place);
          } else {
            reject();
          }
        });
      });
    } catch (error) {
      return Promise.reject()
      console.error('Error fetching place details:', error);
      // throw error instanceof PlacesError ? error : new PlacesError('Failed to fetch place details', 'UNKNOWN_ERROR');
    }
  }

  /**
   * Get coordinates for a place by its ID using geocoder
   * @param placeId The Google Places ID
   * @returns Promise resolving to the latitude and longitude
   */
  async getCoordinatesFromPlaceId(placeId: string): Promise<PlaceLocation> {
    try {
      this.ensureServicesInitialized();

      const result = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
        this.geocoder!.geocode(
          { placeId, region: 'ma' },
          (results, status) => {
            if (status === 'OK' && results?.[0]?.geometry?.location) {
              resolve(results);
            } else {
              reject(new PlacesError(`Geocoding failed: ${status}`, status));
            }
          }
        );
      });

      const location = result[0].geometry.location;
      return {
        lat: location.lat(),
        lng: location.lng()
      };
    } catch (error) {
      console.error('Error getting coordinates:', error);
       return Promise.reject()
      // throw error instanceof PlacesError ? error : new PlacesError('Failed to get coordinates', 'UNKNOWN_ERROR');
    }
  }

  /**
   * Add location data to place prediction
   * @param prediction The place prediction to enhance with location data
   * @returns Promise resolving to the enhanced prediction or null if failed
   */
  private async getPlaceDetails(
    prediction: google.maps.places.AutocompletePrediction
  ): Promise<PlacePrediction | null> {
    try {
      const place = await this.getPlaceById(prediction.place_id);
      return {
        description: prediction.description,
        place_id: prediction.place_id,
        structured_formatting: {
          main_text: prediction.structured_formatting.main_text,
          secondary_text: prediction.structured_formatting.secondary_text
        },
        lat: place.geometry?.location?.lat(),
        lng: place.geometry?.location?.lng()
      };
    } catch (error) {
      console.error('Error getting place details:', error);
      return null;
    }
  }
}
